import { useEffect, useState, useCallback } from 'react';
import MainLayout from '../components/layout/MainLayout';
import { API_BASE_URL } from '../config';

const API = API_BASE_URL;
const HEADERS = { 'Content-Type': 'application/json' };

const GEO_OPTIONS = ['SE', 'NO', 'FI'];
const ACTION_TYPES = [
  { value: 'direct', label: 'Direct → Affiliate' },
  { value: 'comparison_page', label: 'Comparison Page (internal)' },
  { value: 'ab_split', label: 'A/B Split' },
];

const INTERNAL_PAGES: Record<string, {label: string, url: string}[]> = {
  'SE': [
    { label: 'Sambla Whitelabel (/ansok-lan/)', url: 'https://simplyloan.se/ansok-lan/' },
    { label: 'Comparison Table (/lanesammanstallaren/)', url: 'https://simplyloan.se/lanesammanstallaren/' },
    { label: 'Recommended Loans (/utvalda-lan/)', url: 'https://simplyloan.se/utvalda-lan/' }
  ],
  'NO': [
    { label: 'Comparison Table (/lanekalkulatoren/)', url: 'https://simplyloan.no/lanekalkulatoren/' }
  ],
  'FI': [
    { label: 'Comparison Table (/lainalaskuri/)', url: 'https://simplyloan.fi/lainalaskuri/' }
  ]
};

interface Rule {
  id: number;
  name: string;
  description?: string;
  geo: string;
  priority: number;
  is_active: boolean;
  condition_loan_amount_min?: number;
  condition_loan_amount_max?: number;
  condition_loan_term_min?: number;
  condition_loan_term_max?: number;
  condition_is_duplicate?: boolean | null;
  condition_is_low_quality?: boolean | null;
  condition_utm_source?: string;
  condition_utm_medium?: string;
  action_type: string;
  destination_slug?: string;
  destination_url?: string;
  comparison_page_url?: string;
  ab_destinations?: { url?: string; weight: number; label?: string; slug?: string; destination_url?: string }[];
  fallback_url?: string;
  is_fallback: boolean;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
}

const EMPTY_RULE: Partial<Rule> = {
  name: '', geo: 'SE', priority: 100, is_active: true,
  action_type: 'direct', is_fallback: false,
  ab_destinations: [{ weight: 50 }, { weight: 50 }],
};

export default function SimplyloanRedirectAdminPage() {
  const [rules, setRules] = useState<Rule[]>([]);
  const [affiliates, setAffiliates] = useState<{slug: string, name: string}[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterGeo, setFilterGeo] = useState('');
  const [selected, setSelected] = useState<Rule | null>(null);
  const [form, setForm] = useState<Partial<Rule>>(EMPTY_RULE);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dryRun, setDryRun] = useState<any>(null);
  const [dryForm, setDryForm] = useState({ geo: 'SE', loan_amount: 25000, is_duplicate: false, is_low_quality: false, utm_source: '', session_id: 'test_123' });
  const [tab, setTab] = useState<'rules' | 'dryrun'>('rules');

  const fetchRules = useCallback(async () => {
    setLoading(true);
    const geo = filterGeo ? `?geo=${filterGeo}` : '';
    const res = await fetch(`${API}/api/SL_RnTmodule/rules${geo}`, { headers: HEADERS });
    const data = await res.json();
    setRules(data.rules || []);
    setLoading(false);
  }, [filterGeo]);

  const fetchAffiliates = useCallback(async (geo: string) => {
    if (!geo) return;
    try {
      const res = await fetch(`${API}/api/SL_RnTmodule/affiliates?geo=${geo}`, { headers: HEADERS });
      const data = await res.json();
      setAffiliates(data.affiliates || []);
    } catch (e) {
      console.error('Failed to fetch affiliates', e);
      setAffiliates([]);
    }
  }, []);

  useEffect(() => { fetchRules(); }, [fetchRules]);
  
  useEffect(() => {
    if ((isNew || selected) && form.geo) {
      fetchAffiliates(form.geo);
    }
  }, [form.geo, isNew, selected, fetchAffiliates]);

  const saveRule = async () => {
    setSaving(true);
    const url = isNew ? `${API}/api/SL_RnTmodule/rules` : `${API}/api/SL_RnTmodule/rules/${selected?.id}`;
    const method = isNew ? 'POST' : 'PUT';
    await fetch(url, { method, headers: HEADERS, body: JSON.stringify(form) });
    await fetchRules();
    setSaving(false);
    setSelected(null);
    setIsNew(false);
  };

  const toggleRule = async (rule: Rule) => {
    await fetch(`${API}/api/SL_RnTmodule/rules/${rule.id}/toggle`, {
      method: 'POST', headers: HEADERS,
      body: JSON.stringify({ is_active: !rule.is_active }),
    });
    await fetchRules();
  };

  const deleteRule = async (id: number) => {
    if (!window.confirm('Slet denne regel?')) return;
    await fetch(`${API}/api/SL_RnTmodule/rules/${id}`, { method: 'DELETE', headers: HEADERS });
    await fetchRules();
    setSelected(null);
  };

  const runDryRun = async () => {
    const res = await fetch(`${API}/api/SL_RnTmodule/rules/evaluate`, {
      method: 'POST', headers: HEADERS, body: JSON.stringify(dryForm),
    });
    setDryRun(await res.json());
  };

  const openNew = () => {
    setForm(EMPTY_RULE);
    setSelected(null);
    setIsNew(true);
    setTab('rules');
  };

  const openEdit = (rule: Rule) => {
    setForm({ ...rule });
    setSelected(rule);
    setIsNew(false);
    setTab('rules');
  };

  const geoColor: Record<string, string> = {
    SE: 'bg-blue-100 text-blue-700', NO: 'bg-red-100 text-red-700', FI: 'bg-white text-gray-700 border border-gray-200',
  };

  return (
    <MainLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Redirect Admin</h1>
            <p className="text-sm text-gray-500 mt-0.5">SimplyLoan form-submit redirect regel-motor</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setTab(tab === 'dryrun' ? 'rules' : 'dryrun')}
              className={`px-3 py-2 text-sm rounded-lg font-medium border transition-colors ${tab === 'dryrun' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
              🧪 Dry-run test
            </button>
            <button onClick={openNew}
              className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors">
              + Ny regel
            </button>
          </div>
        </div>

        {/* Dry-run panel */}
        {tab === 'dryrun' && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4">
            <h2 className="font-semibold text-gray-800">Test regelmotor</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Geo</label>
                <select value={dryForm.geo} onChange={e => setDryForm(p => ({...p, geo: e.target.value}))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
                  {GEO_OPTIONS.map(g => <option key={g}>{g}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Lånebeløb</label>
                <input type="number" value={dryForm.loan_amount}
                  onChange={e => setDryForm(p => ({...p, loan_amount: +e.target.value}))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"/>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">UTM Source</label>
                <input type="text" value={dryForm.utm_source}
                  onChange={e => setDryForm(p => ({...p, utm_source: e.target.value}))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder="fx email"/>
              </div>
              <div className="flex flex-col gap-2 pt-5">
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  <input type="checkbox" checked={dryForm.is_duplicate}
                    onChange={e => setDryForm(p => ({...p, is_duplicate: e.target.checked}))}/>
                  Duplicate
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  <input type="checkbox" checked={dryForm.is_low_quality}
                    onChange={e => setDryForm(p => ({...p, is_low_quality: e.target.checked}))}/>
                  Low quality
                </label>
              </div>
            </div>
            <button onClick={runDryRun}
              className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700">
              Kør test
            </button>
            {dryRun && (
              <div className={`rounded-lg p-4 text-sm ${dryRun.matched ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
                {dryRun.matched ? (
                  <>
                    <p className="font-semibold text-emerald-700">✅ Regel matchet: {dryRun.rule_name} (ID {dryRun.rule_id})</p>
                    <p className="text-gray-600 mt-1">Type: <strong>{dryRun.action_type}</strong></p>
                    <p className="text-gray-600">Destination: <code className="bg-white px-1 rounded">{dryRun.destination}</code></p>
                  </>
                ) : (
                  <p className="text-amber-700 font-medium">⚠️ Ingen regel matchede — fallback ville blive brugt</p>
                )}
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Rule list */}
          <div className="lg:col-span-3 space-y-3">
            {/* Filter */}
            <div className="flex gap-2">
              {['', ...GEO_OPTIONS].map(g => (
                <button key={g} onClick={() => setFilterGeo(g)}
                  className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${filterGeo === g ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}>
                  {g || 'Alle'}
                </button>
              ))}
              <span className="ml-auto text-sm text-gray-400 self-center">{rules.length} regler</span>
            </div>

            {/* Rules */}
            {loading ? (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400">Henter regler...</div>
            ) : rules.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400">
                Ingen regler endnu. <button onClick={openNew} className="text-indigo-600 hover:underline">Opret den første</button>
              </div>
            ) : (
              <div className="space-y-2">
                {rules.map(rule => (
                  <div key={rule.id}
                    onClick={() => openEdit(rule)}
                    className={`bg-white rounded-xl border p-4 cursor-pointer transition-all hover:shadow-sm ${selected?.id === rule.id ? 'border-indigo-400 shadow-sm' : 'border-gray-200'}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="text-xs font-mono text-gray-400 w-6 shrink-0">#{rule.priority}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold shrink-0 ${geoColor[rule.geo]}`}>{rule.geo}</span>
                        <span className="font-medium text-gray-800 truncate">{rule.name}</span>
                        {rule.is_fallback && <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs rounded shrink-0">fallback</span>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${rule.action_type === 'direct' ? 'bg-purple-50 text-purple-700' : rule.action_type === 'ab_split' ? 'bg-orange-50 text-orange-700' : 'bg-teal-50 text-teal-700'}`}>
                          {rule.action_type}
                        </span>
                        <button onClick={e => { e.stopPropagation(); toggleRule(rule); }}
                          className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors ${rule.is_active ? 'bg-emerald-500' : 'bg-gray-200'}`}>
                          <span className={`inline-block h-4 w-4 mt-0.5 rounded-full bg-white shadow transition-transform ${rule.is_active ? 'translate-x-4' : 'translate-x-0.5'}`}/>
                        </button>
                      </div>
                    </div>
                    {rule.description && <p className="text-xs text-gray-400 mt-1 ml-8 truncate">{rule.description}</p>}
                    <div className="flex gap-3 mt-2 ml-8 text-xs text-gray-400">
                      {rule.condition_loan_amount_min && <span>Min {rule.condition_loan_amount_min.toLocaleString()}</span>}
                      {rule.condition_loan_amount_max && <span>Max {rule.condition_loan_amount_max.toLocaleString()}</span>}
                      {rule.condition_is_duplicate === true && <span className="text-red-500">duplicate</span>}
                      {rule.condition_is_low_quality === true && <span className="text-orange-500">low_quality</span>}
                      {rule.destination_slug && <span className="text-purple-500">{rule.destination_slug}</span>}
                      {rule.comparison_page_url && <span className="text-teal-600">{rule.comparison_page_url}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Editor panel */}
          {(selected || isNew) && (
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4 sticky top-6">
                <div className="flex items-center justify-between">
                  <h2 className="font-semibold text-gray-800">{isNew ? 'Ny regel' : 'Rediger regel'}</h2>
                  <button onClick={() => { setSelected(null); setIsNew(false); }} className="text-gray-400 hover:text-gray-600 text-lg">×</button>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">Navn *</label>
                    <input value={form.name || ''} onChange={e => setForm(p => ({...p, name: e.target.value}))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder="fx Lendo fallback SE"/>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Geo *</label>
                      <select value={form.geo || 'SE'} onChange={e => setForm(p => ({...p, geo: e.target.value}))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
                        {GEO_OPTIONS.map(g => <option key={g}>{g}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Prioritet</label>
                      <input type="number" value={form.priority ?? 100} onChange={e => setForm(p => ({...p, priority: +e.target.value}))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"/>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">Beskrivelse</label>
                    <textarea value={form.description || ''} onChange={e => setForm(p => ({...p, description: e.target.value}))}
                      rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none"/>
                  </div>

                  <hr className="border-gray-100"/>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Betingelser</p>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Beløb min</label>
                      <input type="number" value={form.condition_loan_amount_min ?? ''} onChange={e => setForm(p => ({...p, condition_loan_amount_min: e.target.value ? +e.target.value : undefined}))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder="fx 45000"/>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Beløb max</label>
                      <input type="number" value={form.condition_loan_amount_max ?? ''} onChange={e => setForm(p => ({...p, condition_loan_amount_max: e.target.value ? +e.target.value : undefined}))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder="fx 70000"/>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Løbetid min (år)</label>
                      <input type="number" value={form.condition_loan_term_min ?? ''} onChange={e => setForm(p => ({...p, condition_loan_term_min: e.target.value ? +e.target.value : undefined}))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder="fx 1"/>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Løbetid max (år)</label>
                      <input type="number" value={form.condition_loan_term_max ?? ''} onChange={e => setForm(p => ({...p, condition_loan_term_max: e.target.value ? +e.target.value : undefined}))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder="fx 5"/>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Duplicate</label>
                      <select value={form.condition_is_duplicate === true ? 'true' : form.condition_is_duplicate === false ? 'false' : ''}
                        onChange={e => setForm(p => ({...p, condition_is_duplicate: e.target.value === '' ? undefined : e.target.value === 'true'}))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
                        <option value="">Ligegyldig</option>
                        <option value="true">Ja (duplicate)</option>
                        <option value="false">Nej (ikke duplicate)</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Low quality</label>
                      <select value={form.condition_is_low_quality === true ? 'true' : form.condition_is_low_quality === false ? 'false' : ''}
                        onChange={e => setForm(p => ({...p, condition_is_low_quality: e.target.value === '' ? undefined : e.target.value === 'true'}))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
                        <option value="">Ligegyldig</option>
                        <option value="true">Ja</option>
                        <option value="false">Nej</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">UTM Source (tom = alle)</label>
                    <input value={form.condition_utm_source || ''} onChange={e => setForm(p => ({...p, condition_utm_source: e.target.value}))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder="fx email"/>
                  </div>

                  <hr className="border-gray-100"/>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Handling</p>

                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">Type</label>
                    <select value={form.action_type || 'direct'} onChange={e => setForm(p => ({...p, action_type: e.target.value}))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
                      {ACTION_TYPES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                    </select>
                  </div>

                  {form.action_type === 'direct' && (
                    <>
                      <div>
                        <label className="text-xs font-medium text-gray-500 mb-1 block">Affiliate slug</label>
                        {affiliates.length > 0 ? (
                          <select value={form.destination_slug || ''} onChange={e => setForm(p => ({...p, destination_slug: e.target.value}))}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono">
                            <option value="" disabled>-- Vælg slug --</option>
                            {affiliates.map(a => (
                              <option key={a.slug} value={a.slug}>{a.name} ({a.slug})</option>
                            ))}
                          </select>
                        ) : (
                          <input value={form.destination_slug || ''} onChange={e => setForm(p => ({...p, destination_slug: e.target.value}))}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono" placeholder="lendo-se"/>
                        )}
                        <p className="text-xs text-gray-400 mt-1">Vælg fra aktive affiliate links for den valgte geo</p>
                      </div>
                      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                        <label className="text-xs font-semibold text-indigo-700 mb-1 block">🔗 Direkte URL (override)</label>
                        <input value={form.destination_url || ''} onChange={e => setForm(p => ({...p, destination_url: e.target.value}))}
                          className="w-full border border-indigo-200 bg-white rounded-lg px-3 py-2 text-sm font-mono" placeholder="https://lendo.se/ansok/?ref=simplyloan&..."/>
                        <p className="text-xs text-indigo-500 mt-1">Indsæt fuld URL direkte — bruges frem for slug hvis udfyldt</p>
                        {INTERNAL_PAGES[form.geo || 'SE'] && (
                          <select 
                            value="" 
                            onChange={e => {
                              if(e.target.value) setForm(p => ({...p, destination_url: e.target.value}));
                            }}
                            className="w-full border border-indigo-200 bg-indigo-50/50 rounded-lg px-3 py-2 text-sm text-indigo-700 mt-2 cursor-pointer outline-none"
                          >
                            <option value="">-- Eller genvej: Indsæt en intern side --</option>
                            {INTERNAL_PAGES[form.geo || 'SE'].map(page => (
                              <option key={page.url} value={page.url}>{page.label}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    </>
                  )}

                  {form.action_type === 'comparison_page' && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Comparison page URL Preview</label>
                      <input disabled value={
                        form.geo === 'SE' ? 'https://simplyloan.se/lanesammanstallaren/' :
                        form.geo === 'NO' ? 'https://simplyloan.no/lanekalkulatoren/' :
                        form.geo === 'FI' ? 'https://simplyloan.fi/lainalaskuri/' : ''
                      }
                        className="w-full border border-gray-200 bg-gray-50 text-gray-500 rounded-lg px-3 py-2 text-sm font-mono cursor-not-allowed" />
                      <p className="text-xs text-gray-400 mt-1">session_id og lånedetaljer tilføjes automatisk ved redirect.</p>
                    </div>
                  )}

                  {form.action_type === 'ab_split' && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">A/B destinationer</label>
                        <span className={`text-xs font-medium ${
                          (form.ab_destinations || []).reduce((s, d) => s + (d.weight || 0), 0) === 100
                            ? 'text-emerald-600' : 'text-red-500'
                        }`}>
                          Total: {(form.ab_destinations || []).reduce((s, d) => s + (d.weight || 0), 0)}%
                        </span>
                      </div>
                      {(form.ab_destinations || []).map((dest, i) => (
                        <div key={i} className="bg-gray-50 rounded-lg p-3 space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-gray-400 w-5">{String.fromCharCode(65 + i)}</span>
                            <input
                              value={dest.label || ''}
                              onChange={e => {
                                const updated = [...(form.ab_destinations || [])];
                                updated[i] = { ...updated[i], label: e.target.value };
                                setForm(p => ({ ...p, ab_destinations: updated }));
                              }}
                              className="flex-1 border border-gray-200 rounded px-2 py-1 text-xs" placeholder="Navn (fx Lendo)"
                            />
                            <input
                              type="number" min={0} max={100}
                              value={dest.weight}
                              onChange={e => {
                                const updated = [...(form.ab_destinations || [])];
                                updated[i] = { ...updated[i], weight: +e.target.value };
                                setForm(p => ({ ...p, ab_destinations: updated }));
                              }}
                              className="w-16 border border-gray-200 rounded px-2 py-1 text-xs text-center font-mono"
                            />
                            <span className="text-xs text-gray-400">%</span>
                            {(form.ab_destinations || []).length > 2 && (
                              <button onClick={() => {
                                const updated = (form.ab_destinations || []).filter((_, j) => j !== i);
                                setForm(p => ({ ...p, ab_destinations: updated }));
                              }} className="text-red-400 hover:text-red-600 text-sm px-1">×</button>
                            )}
                          </div>
                          <div className="space-y-3 mt-3">
                            <div>
                              <label className="text-xs font-semibold text-gray-700 mb-1 block">Destination Slug</label>
                              <select
                                value={dest.slug || ''}
                                onChange={e => {
                                  const updated = [...(form.ab_destinations || [])];
                                  updated[i] = { ...updated[i], slug: e.target.value };
                                  setForm(p => ({ ...p, ab_destinations: updated }));
                                }}
                                className="w-full border border-gray-300 bg-white rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                              >
                                <option value="">-- Vælg affiliate link --</option>
                                {affiliates.map(a => (
                                  <option key={a.slug} value={a.slug}>{a.name}</option>
                                ))}
                              </select>
                            </div>
                            <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3">
                              <label className="text-xs font-semibold text-indigo-700 mb-1 block">🔗 Direkte URL (override)</label>
                              <input
                                value={dest.destination_url || dest.url || ''}
                                onChange={e => {
                                  const updated = [...(form.ab_destinations || [])];
                                  updated[i] = { ...updated[i], destination_url: e.target.value, url: e.target.value };
                                  setForm(p => ({ ...p, ab_destinations: updated }));
                                }}
                                className="w-full border border-indigo-200 bg-white rounded-lg px-3 py-2 text-sm font-mono"
                                placeholder="https://simplyloan.se/xxxxx"
                              />
                              {INTERNAL_PAGES[form.geo || 'SE'] && (
                                <select 
                                  value="" 
                                  onChange={e => {
                                    if(e.target.value) {
                                      const updated = [...(form.ab_destinations || [])];
                                      updated[i] = { ...updated[i], destination_url: e.target.value, url: e.target.value };
                                      setForm(p => ({ ...p, ab_destinations: updated }));
                                    }
                                  }}
                                  className="w-full border border-indigo-200 bg-indigo-50/50 rounded-lg px-3 py-2 text-xs text-indigo-700 mt-2 cursor-pointer outline-none"
                                >
                                  <option value="">-- Eller genvej: Indsæt en intern side --</option>
                                  {INTERNAL_PAGES[form.geo || 'SE'].map(page => (
                                    <option key={page.url} value={page.url}>{page.label}</option>
                                  ))}
                                </select>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      <button
                        onClick={() => setForm(p => ({ ...p, ab_destinations: [...(p.ab_destinations || []), { weight: 0 }] }))}
                        className="w-full py-1.5 border border-dashed border-gray-300 rounded-lg text-xs text-gray-500 hover:border-indigo-400 hover:text-indigo-600 transition-colors"
                      >
                        + Tilføj destination
                      </button>
                    </div>
                  )}

                  <hr className="border-gray-100"/>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <label className="text-xs font-semibold text-amber-700 mb-1 block">⚡ Fallback URL</label>
                    <input value={(form as any).fallback_url || ''}
                      onChange={e => setForm(p => ({ ...p, fallback_url: e.target.value } as any))}
                      className="w-full border border-amber-200 bg-white rounded-lg px-3 py-2 text-sm font-mono"
                      placeholder="https://... (bruges hvis ingen regler matcher)"/>
                    <p className="text-xs text-amber-600 mt-1">Global nødventil — bruges KUN hvis ingen anden regel matcher for dette geo</p>
                  </div>

                  <div className="flex items-center gap-4 pt-1">
                    <label className="flex items-center gap-2 text-sm text-gray-600">
                      <input type="checkbox" checked={form.is_fallback || false}
                        onChange={e => setForm(p => ({...p, is_fallback: e.target.checked}))}/>
                      Fallback-regel
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-600">
                      <input type="checkbox" checked={form.is_active !== false}
                        onChange={e => setForm(p => ({...p, is_active: e.target.checked}))}/>
                      Aktiv
                    </label>
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <button onClick={saveRule} 
                    disabled={saving || !form.name || !form.geo || 
                      (form.action_type === 'direct' && !form.destination_slug && !form.destination_url) ||
                      (form.action_type === 'ab_split' && !(form.ab_destinations || []).every(d => d.slug || d.destination_url))
                    }
                    className="flex-1 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
                    {saving ? 'Gemmer...' : isNew ? 'Opret regel' : 'Gem ændringer'}
                  </button>
                  {!isNew && selected && (
                    <button onClick={() => deleteRule(selected.id)}
                      className="px-3 py-2 text-red-500 hover:bg-red-50 rounded-lg text-sm transition-colors">
                      Slet
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
